import type { FileUploadError as FileUploadErrorType } from "@/utils/fileUploadErrorParser";

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@heroui/react";

export interface FileUploadErrorProps {
  /** Parsed error information */
  error: FileUploadErrorType;
  /** Callback for retry action */
  onRetry?: () => void;
  /** Callback for remove/dismiss action */
  onRemove?: () => void;
  /** Optional CSS class name */
  className?: string;
  /** Show retry button */
  showRetry?: boolean;
  /** The file has used all of its retries: Retry is hidden and a message says so */
  retryLimitReached?: boolean;
  /** Show remove button */
  showRemove?: boolean;
}

type TechnicalDetails = FileUploadErrorType["technicalDetails"];

interface ErrorSuggestionsProps {
  suggestions?: string[];
}

/** The suggestion list, which renders nothing when there are no suggestions. */
const ErrorSuggestions: React.FC<ErrorSuggestionsProps> = ({ suggestions }) => {
  const { t } = useTranslation();

  if (!suggestions || suggestions.length === 0) return null;

  return (
    <div className="mt-3">
      <p className="text-sm font-medium">
        {t("fileUpload.suggestions", { defaultValue: "Suggestions:" })}
      </p>
      <ul className="mt-1 list-disc list-inside text-sm space-y-1">
        {suggestions.map((suggestion, index) => (
          <li key={index}>{suggestion}</li>
        ))}
      </ul>
    </div>
  );
};

interface TechnicalDetailsToggleProps {
  isOpen: boolean;
  onToggle: () => void;
}

const TechnicalDetailsToggle: React.FC<TechnicalDetailsToggleProps> = ({ isOpen, onToggle }) => {
  const { t } = useTranslation();

  return (
    <div className="mt-3">
      <button
        className="text-sm font-medium underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger focus-visible:ring-offset-2 rounded"
        data-testid="toggle-technical-details"
        type="button"
        onClick={onToggle}
      >
        {isOpen
          ? t("fileUpload.hideTechnicalDetails", { defaultValue: "Hide" })
          : t("fileUpload.showTechnicalDetails", { defaultValue: "Show" })}{" "}
        {t("fileUpload.technicalDetailsLabel", { defaultValue: "Technical Details" })}{" "}
        <span aria-hidden="true">{isOpen ? "▲" : "▼"}</span>
      </button>
    </div>
  );
};

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
  <div>
    <span className="font-semibold">{label}</span> {value}
  </div>
);

interface TechnicalDetailsPanelProps {
  details: TechnicalDetails;
}

/**
 * The expanded technical details. Each optional row is gated on the value's
 * truthiness rather than its presence, so a status of 0 renders as a bare "0" text
 * node and an empty string renders nothing. `FileUploadError.golden.test.tsx` pins
 * both quirks.
 */
const TechnicalDetailsPanel: React.FC<TechnicalDetailsPanelProps> = ({ details }) => {
  const { t } = useTranslation();

  return (
    <div
      className="mt-3 p-3 bg-default-100 dark:bg-default-100/50 rounded text-xs font-mono space-y-1"
      data-testid="technical-details"
    >
      {details.errorCode && (
        <DetailRow
          label={t("fileUpload.errorCode", { defaultValue: "Error Code:" })}
          value={details.errorCode}
        />
      )}
      {details.status && (
        <DetailRow
          label={t("fileUpload.status", { defaultValue: "Status:" })}
          value={details.status}
        />
      )}
      {details.fileName && (
        <DetailRow
          label={t("fileUpload.file", { defaultValue: "File:" })}
          value={details.fileName}
        />
      )}
      {details.fileSize && (
        <DetailRow
          label={t("fileUpload.size", { defaultValue: "Size:" })}
          value={details.fileSize}
        />
      )}
      {details.originalMessage && (
        <DetailRow
          label={t("fileUpload.message", { defaultValue: "Message:" })}
          value={details.originalMessage}
        />
      )}
      <DetailRow
        label={t("fileUpload.timestamp", { defaultValue: "Timestamp:" })}
        value={details.timestamp}
      />
    </div>
  );
};

interface ErrorActionsProps {
  showRetry: boolean;
  showRemove: boolean;
  onRetry?: () => void;
  onRemove?: () => void;
}

/**
 * The retry and remove buttons. The row itself renders whenever either switch is
 * on, even when neither callback is supplied, which leaves an empty row.
 */
const ErrorActions: React.FC<ErrorActionsProps> = ({
  showRetry,
  showRemove,
  onRetry,
  onRemove,
}) => {
  const { t } = useTranslation();

  if (!showRetry && !showRemove) return null;

  return (
    <div className="mt-4 flex gap-2">
      {showRetry && onRetry && (
        <Button data-testid="retry-button" size="sm" variant="danger-soft" onPress={onRetry}>
          {t("fileUpload.retryUpload", { defaultValue: "Retry Upload" })}
        </Button>
      )}
      {showRemove && onRemove && (
        <Button data-testid="remove-button" size="sm" variant="ghost" onPress={onRemove}>
          {t("fileUpload.removeFile", { defaultValue: "Remove File" })}
        </Button>
      )}
    </div>
  );
};

/**
 * File Upload Error Component
 *
 * Displays user-friendly error messages with collapsible technical details.
 * Follows STYLE-GUIDE.md patterns:
 * - Uses HeroUI components
 * - No text color overrides (let HeroUI handle theming)
 * - Responsive design
 *
 * The sections render in document order after this component's own heading, so the
 * `t()` calls keep document order. The open state lives here rather than in the
 * toggle, so opening the details re-renders the whole error.
 */
export const FileUploadError: React.FC<FileUploadErrorProps> = ({
  error,
  onRetry,
  onRemove,
  className = "",
  showRetry = true,
  showRemove = true,
  retryLimitReached = false,
}) => {
  const { t } = useTranslation();
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);

  return (
    <div
      className={`border border-danger/30 bg-danger-50 dark:bg-danger-50/10 p-4 rounded-lg ${className}`}
      data-testid="file-upload-error"
      role="alert"
    >
      {/* Error Icon and Message */}
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg
            aria-hidden="true"
            className="h-5 w-5 text-danger"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              clipRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              fillRule="evenodd"
            />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-danger" data-testid="error-message">
            {t("fileUpload.uploadFailed", { defaultValue: "Upload Failed" })}
            {error.technicalDetails.fileName ? `: ${error.technicalDetails.fileName}` : ""}
          </h3>
          <div className="mt-2 text-sm" data-testid="error-description">
            {error.userMessage}
          </div>

          {retryLimitReached ? (
            <p className="mt-2 text-sm font-medium" data-testid="retry-limit-message">
              {t("fileUpload.retryLimitReached", {
                defaultValue:
                  "This upload could not be completed after several attempts. Please remove the file and try again later, or use a different file.",
              })}
            </p>
          ) : (
            /* Suggestions */
            <ErrorSuggestions suggestions={error.suggestions} />
          )}

          {/* Technical Details Toggle */}
          <TechnicalDetailsToggle
            isOpen={showTechnicalDetails}
            onToggle={() => setShowTechnicalDetails(!showTechnicalDetails)}
          />

          {/* Technical Details Content */}
          {showTechnicalDetails && <TechnicalDetailsPanel details={error.technicalDetails} />}

          {/* Action Buttons */}
          <ErrorActions
            showRemove={showRemove}
            showRetry={showRetry && !retryLimitReached}
            onRemove={onRemove}
            onRetry={onRetry}
          />
        </div>
      </div>
    </div>
  );
};
